import React, { useState, useCallback, useEffect } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { createRemoteRequest, editRemoteRequestCurrentUser, getMyRemoteRequests, getAllRemoteRequests } from "../../slices/remoteRequest";
import { debounce } from "lodash";
import CalendarIcon from "../../assets/icons/Calendar.png";
import "./_remoteRequest.scss";
import Button from "../../components/Button/index";
import DatePicker from "../../components/Form/DatePicker";
import Textarea from "../../components/Form/Textarea";
import SubTitle from "../../components/SubTitle/SubTitle.jsx";
import Notice from "../../components/Notice";
import RemoteWorkProgressBar from "./RemoteWorkProgressBar";
import pluralHandler from "../../utilities/pluralHandler";

const FormRemoteRequest = (props) => {
  const { handleClose, id, row, disabled, updateMyRemoteRequest } = props;
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notice, setNotice] = useState({ open: false, text: "", state: "" });

  useEffect(() => {
    setTimeout(() => {
      setNotice((prevState) => ({
        ...prevState,
        open: false,
      }));
    }, 6000);
  }, [notice]);

  // Get user data (includes remoteDays field)
  const user = useSelector((state) => state.user.fetchMe);

  const initialValues = {
    remoteDate: {
      from: row
        ? {
            day: new Date(row?.startDate).getDate(),
            month: new Date(row?.startDate).getMonth() + 1,
            year: new Date(row?.startDate).getFullYear(),
          }
        : null,
      to: row
        ? {
            day: new Date(row?.endDate).getDate(),
            month: new Date(row?.endDate).getMonth() + 1,
            year: new Date(row?.endDate).getFullYear(),
          }
        : null,
    },
    reason: row ? row.reason : "",
  };

  const validationSchema = Yup.object({
    remoteDate: Yup.object({
      from: Yup.object().nullable().required("Start date is required"),
      to: Yup.object().nullable().required("End date is required"),
    }).test('max-days', 'Remote work request cannot exceed 10 days', function(value) {
      if (!value?.from || !value?.to) return true; // Let required validation handle missing values
      
      const days = calculateDays(value.from, value.to);
      return days <= 10;
    }),
    reason: Yup.string()
      .required("Reason is required")
      .min(10, "Reason must be at least 10 characters")
      .max(300, "Reason cannot exceed 300 characters"),
  });

  // Calculate number of days between dates
  const calculateDays = (fromDate, toDate) => {
    if (!fromDate || !toDate) return 1;
    const startDate = new Date(fromDate.year, fromDate.month - 1, fromDate.day);
    const endDate = new Date(toDate.year, toDate.month - 1, toDate.day);
    const diffTime = Math.abs(endDate - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  };

  // Debounced submit function to prevent rapid submissions
  const debouncedSubmit = useCallback(
    debounce(async (values, submitProps) => {
      if (isSubmitting) return;
      
      setIsSubmitting(true);
      submitProps.setSubmitting(true);
      
      try {
        const numberOfDays = calculateDays(values.remoteDate.from, values.remoteDate.to);
        const data = {
          startDate: `${values.remoteDate.from.month}/${values.remoteDate.from.day}/${values.remoteDate.from.year}`,
          endDate: `${values.remoteDate.to.month}/${values.remoteDate.to.day}/${values.remoteDate.to.year}`,
          reason: values.reason.trim(),
          numberOfDays: numberOfDays,
        };

        if (updateMyRemoteRequest) {
          const res = await dispatch(editRemoteRequestCurrentUser({ data, id: row._id }));
          if (res.error) {
            setNotice({
              text: res?.error?.error || "An error occurred while updating your request. Please try again.",
              open: true,
              state: "error",
            });
            submitProps.setStatus({ success: false });
          } else {
            if (res?.payload?.message) {
              setNotice({
                text: res?.payload?.message,
                open: true,
                state: "success",
              });
              submitProps.setStatus({ success: true });
              setTimeout(() => {
                handleClose(id);
              }, 1500);
            }
          }
        } else {
          const res = await dispatch(createRemoteRequest(data));
          
          if (res?.error) {
            setNotice({
              text: res?.error?.error || res?.error?.message || "An error occurred while submitting your request. Please try again.",
              open: true,
              state: "error",
            });
            submitProps.setStatus({ success: false });
          } else {
            setNotice({
              text: res?.payload?.message || "Your remote work request is successfully submitted!",
              open: true,
              state: "success",
            });
            submitProps.setStatus({ success: true });
            
            // Refresh the data to ensure the new request appears immediately
            dispatch(getMyRemoteRequests());
            dispatch(getAllRemoteRequests());
            
            setTimeout(() => {
              handleClose(id);
            }, 1500);
          }
        }
      } catch (error) {
        setNotice({
          text: "An error occurred while submitting your request. Please try again.",
          open: true,
          state: "error",
        });
        submitProps.setStatus({ success: false });
      } finally {
        setIsSubmitting(false);
        submitProps.setSubmitting(false);
      }
    }, 1000),
    [isSubmitting, dispatch, handleClose, id, updateMyRemoteRequest, row]
  );

  const onSubmit = async (values, submitProps) => {
    debouncedSubmit(values, submitProps);
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {(formik) => {
        return (
          <>
            <Notice
              open={notice.open}
              text={notice.text}
              state={notice.state}
            />

            <Form style={{marginBottom:"1.75rem"}}>
              <div className="negotiation-form-wrapper-details negotiation-form-wrapper">
                {(!disabled && user?.remoteDays !== undefined) || (disabled && row?.user) ? (
                  <div className="BorderLinearProgressWrapper">
                    <SubTitle subTitle={"Remote Work Balance"} />
                    <RemoteWorkProgressBar
                      usedDays={(() => {
                        // For team leads and HR viewing someone else's request, use the request owner's balance
                        const targetUser = disabled && row?.user ? row.user : user;
                        if (targetUser?.remoteDays === undefined || targetUser?.remoteDays === null) return 0;
                        
                        // Correct logic based on your explanation:
                        // remoteDays = 0 means used all 10 days (0 remaining)
                        // remoteDays = 3 means 3 remaining (used 7 days)
                        // remoteDays = -2 means 2 days over limit (used 12 days)
                        if (targetUser.remoteDays < 0) {
                          // Negative means over limit: used days = 10 + absolute value
                          return 10 + Math.abs(targetUser.remoteDays);
                        } else {
                          // Positive or zero means: used days = 10 - remaining days
                          return 10 - targetUser.remoteDays;
                        }
                      })()}
                      totalDays={10}
                      isLimitReached={(() => {
                        const targetUser = disabled && row?.user ? row.user : user;
                        return targetUser?.remoteDays < 0;
                      })()}
                    />
                  </div>
                ) : null}
                <div className="datepicker_wrapper">
                  <DatePicker
                    calendarPopperPosition="bottom"
                    formik={formik}
                    type="date"
                    label="Remote Work Date"
                    name="remoteDate"
                    withIcon
                    rightIcon={CalendarIcon}
                    placeholder="Select Date Range"
                    className={
                      disabled
                        ? "diableddescription negotiation-date"
                        : "costum-date-picker"
                    }
                    error={
                      formik.touched?.remoteDate?.from && formik.errors.remoteDate
                        ? formik.errors.remoteDate.from || formik.errors.remoteDate.to
                        : null
                    }
                  />
                  {formik.values.remoteDate?.from && formik.values.remoteDate?.to && (
                    <div className="date-range-info">
                      <div className={`days-selected ${calculateDays(formik.values.remoteDate.from, formik.values.remoteDate.to) > 10 ? 'days-exceeded' : ''}`}>
                        <span className="days-count">
                          {calculateDays(formik.values.remoteDate.from, formik.values.remoteDate.to)} {pluralHandler(calculateDays(formik.values.remoteDate.from, formik.values.remoteDate.to), "day")} selected
                        </span>
                        {calculateDays(formik.values.remoteDate.from, formik.values.remoteDate.to) > 10 && (
                          <span className="limit-warning">⚠️ Maximum 10 days allowed</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <SubTitle subTitle={"Reason for Remote Work"} />
                <Textarea
                  className={disabled ? "diableddescription" : ""}
                  type="textarea"
                  placeholder="Add your reason for remote work..."
                  name="reason"
                />
                {!disabled && (
                  <div className="submit-button-container">
                    <Button
                      text={updateMyRemoteRequest ? "Update Remote Work Request" : "Apply for Remote Work"}
                      type="submit"
                      className={
                        !formik.isValid || formik.isSubmitting || isSubmitting
                          ? " btn-leave-request blue-disabled-button "
                          : "BlueButton btn-leave-request"
                      }
                      disabled={isSubmitting || formik.isSubmitting}
                    />
                  </div>
                )}
              </div>
            </Form>
          </>
        );
      }}
    </Formik>
  );
};

export default FormRemoteRequest;